import {
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    AlignmentType,
    VerticalAlign,
    HeightRule,
    TableLayoutType,
    ShadingType,
} from "docx";
import { fixColor, fitImage, fetchImageWithDimensions, FULL_WIDTH, SCALE, createCommonHeader, createCommonFooter } from "../../common.js";

export const createTOCTemp1 = async (tocData, startPage) => {
    const style = tocData.style || {};
    const sections = [];

    // Layout constants
    const SPACER_HEIGHT = 100;
    const BODY_HEIGHT = 14570;

    const CONTENT_WIDTH = FULL_WIDTH;
    const LEFT_COL_WIDTH_PERCENT = 48;
    const RIGHT_COL_WIDTH_PERCENT = 48;

    const leftColWidthDxa = Math.floor(CONTENT_WIDTH * (LEFT_COL_WIDTH_PERCENT / 100));
    const rightColWidthDxa = Math.floor(CONTENT_WIDTH * (RIGHT_COL_WIDTH_PERCENT / 100));

    // Estimation Constants (Incorporate SCALE for accuracy)
    const pointMultiplier = 11;
    const baseFontSize = style.text?.size || 12;
    const renderedFontSize = baseFontSize * SCALE;
    const LINE_EST_HEIGHT = renderedFontSize * pointMultiplier;
    const ITEM_SPACING = 200;
    const CHARS_PER_LINE = Math.floor(1000 / renderedFontSize);
    const MAX_PAGE_HEIGHT = 15000;
    const BANNER_HEIGHT_DXA = 800;

    const projects = tocData.projects || [];
    let itemQueue = [...(tocData.pages || [])];
    let projIndex = 0;
    let pageCount = 0;
    let currentAbsPage = startPage || 1;

    while (itemQueue.length > 0 && pageCount < 30) {
        pageCount++;

        // 1. Determine items for this page based on height estimation
        const pageItems = [];
        let currentHeight = 0;

        while (itemQueue.length > 0) {
            const item = itemQueue[0];
            const text = item.heading || "";
            const numLines = Math.ceil(text.length / CHARS_PER_LINE) || 1;
            const itemHeight = (numLines * LINE_EST_HEIGHT) + ITEM_SPACING;

            if (currentHeight + itemHeight <= MAX_PAGE_HEIGHT) {
                pageItems.push(itemQueue.shift());
                currentHeight += itemHeight;
            } else {
                break;
            }
        }

        // 2. Projects for this page (2 per page)
        const pageProjects = [];
        for (let i = 0; i < 2; i++) {
            pageProjects.push(projects[projIndex++] || null);
        }

        // ================= LEFT COLUMN CONTENT =================
        const leftColChildren = [
            new Table({
                width: { size: 100, type: WidthType.PERCENTAGE },
                layout: TableLayoutType.FIXED,
                borders: {
                    top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                    left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                    insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                },
                rows: pageItems.map(item => new TableRow({
                    children: [
                        // Number
                        new TableCell({
                            width: { size: 600, type: WidthType.DXA },
                            children: [new Paragraph({
                                children: [new TextRun({
                                    text: String(item.page).padStart(2, '0'),
                                    bold: true,
                                    size: (style.label?.size || 12) * SCALE,
                                    color: fixColor(style.label?.color || "#2E75B6"),
                                    font: style.label?.font,
                                    underline: style.label?.underline,
                                    italics: style.label?.italic,
                                })]
                            })]
                        }),
                        // Heading
                        new TableCell({
                            width: { size: leftColWidthDxa - 600, type: WidthType.DXA },
                            children: [new Paragraph({
                                spacing: { after: 200 },
                                children: [new TextRun({
                                    text: item.heading || "",
                                    size: (style.text?.size || 12) * SCALE,
                                    color: fixColor(style.text?.color || "#2E75B6"),
                                    font: style.text?.font,
                                    underline: style.text?.underline,
                                    italics: style.text?.italic,
                                })]
                            })]
                        })
                    ]
                }))
            })
        ];

        // ================= RIGHT COLUMN CONTENT =================
        const rightColChildren = [];
        const IMG_BOX_HEIGHT = 4500;

        for (const proj of pageProjects) {
            let projImgRun = null;
            const photo = proj?.uploadedPhoto || proj?.photoURL;
            if (proj && photo) {
                const imgData = await fetchImageWithDimensions(photo);
                if (imgData) {
                    projImgRun = fitImage(imgData, rightColWidthDxa - 400, IMG_BOX_HEIGHT, "cover");
                }
            }

            rightColChildren.push(
                new Table({
                    margins: { top: 0, bottom: 0, left: 0, right: 400 },
                    width: { size: rightColWidthDxa - 400, type: WidthType.DXA },
                    layout: TableLayoutType.FIXED,
                    borders: {
                        top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                        insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                    },
                    rows: [
                        new TableRow({
                            height: { value: IMG_BOX_HEIGHT, rule: HeightRule.EXACT },
                            children: [new TableCell({
                                width: { size: rightColWidthDxa, type: WidthType.DXA },
                                shading: projImgRun ? undefined : { fill: fixColor(tocData.style.colorScheme?.body) },
                                children: [new Paragraph({
                                    alignment: AlignmentType.CENTER,
                                    children: projImgRun ? [projImgRun] : []
                                })]
                            })]
                        })
                    ]
                })
            );
            rightColChildren.push(new Paragraph({ spacing: { after: 400 }, children: [] }));
        }

        // ================= ASSEMBLE PAGE =================
        const headerTable = createCommonHeader(tocData.title, fixColor(tocData.style.colorScheme?.header));

        const bodyTable = new Table({
            width: { size: FULL_WIDTH, type: WidthType.DXA },
            borders: {
                top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
            },
            rows: [new TableRow({
                height: { value: BODY_HEIGHT, rule: HeightRule.EXACT },
                children: [
                    new TableCell({ width: { size: 400, type: WidthType.DXA }, children: [] }),
                    new TableCell({
                        width: { size: leftColWidthDxa, type: WidthType.DXA },
                        verticalAlign: VerticalAlign.CENTER,
                        children: leftColChildren
                    }),
                    new TableCell({ width: { size: 400, type: WidthType.DXA }, children: [] }),
                    new TableCell({
                        width: { size: rightColWidthDxa, type: WidthType.DXA },
                        verticalAlign: VerticalAlign.CENTER,
                        children: rightColChildren
                    }),
                    new TableCell({ width: { size: 400, type: WidthType.DXA }, children: [] }),
                ]
            })]
        });

        const footerTable =
            new TableRow({
                height: { value: BANNER_HEIGHT_DXA, rule: HeightRule.EXACT },
                children: [
                    new TableCell({
                        shading: { fill: tocData.style.colorScheme?.footer, type: ShadingType.CLEAR },
                        verticalAlign: VerticalAlign.BOTTOM,
                        children: [],
                    }),
                ]
            })

        const pageTable = new Table({
            width: { size: FULL_WIDTH, type: WidthType.DXA },
            layout: TableLayoutType.FIXED,
            borders: {
                top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
            },
            rows: [
                new TableRow({
                    children: [
                        new TableCell({
                            children: [
                                headerTable,
                                new Paragraph({ spacing: { before: SPACER_HEIGHT, after: 0 }, children: [] }),
                                bodyTable,
                                new Paragraph({ spacing: { before: SPACER_HEIGHT, after: 0 }, children: [] }),
                                footerTable
                            ]
                        })
                    ]
                })
            ]
        });

        sections.push({
            properties: { page: { margin: { top: 0, right: 0, bottom: 0, left: 0 } } },
            children: [pageTable]
        });
        currentAbsPage++;
    }

    return sections;
};
